import { Injectable, Inject, PLATFORM_ID } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { HttpClient } from '@angular/common/http';
import { LoginResponse } from './auth-models';
import { CORE_CONFIG } from '../environment';
import { tap } from 'rxjs/operators';
import { Customer } from '../customer/customer-models';
import { JwtHelperService } from '@auth0/angular-jwt';
import { isPlatformBrowser } from '@angular/common';

@Injectable({
  providedIn: 'root'
})
export class AuthService {

  isLoggedIn = new BehaviorSubject<boolean>(false);

  permissions = [];

  roles: string[] = [];

  token: string;

  username: string;

  private _loginURL: string;

  private _logoutURL: string;

  private _changePasswordURL: string;

  private _registerURL: string;

  private _activateURL: string;

  private _resetActivationTokenURL: string;

  private _resetPasswordURL: string;

  private _forgotPasswordURL: string;

  private _silentLoginPermissionsURL: string;

  constructor(private httpClient: HttpClient,
    @Inject(PLATFORM_ID) private platformId: string,
    @Inject(CORE_CONFIG) private coreConfig: any) {
    this._loginURL = `${this.coreConfig.endpoint}auth/v1/login`;
    this._logoutURL = `${this.coreConfig.endpoint}auth/v1/logout`;
    this._changePasswordURL = `${this.coreConfig.endpoint}auth/v1/changepassword`;
    this._registerURL = `${this.coreConfig.endpoint}auth/v1/register`;
    this._activateURL = `${this.coreConfig.endpoint}auth/v1/activate`;
    this._resetActivationTokenURL = `${this.coreConfig.endpoint}auth/v1/resetactivationtoken`;
    this._resetPasswordURL = `${this.coreConfig.endpoint}auth/v1/resetpassword`;
    this._forgotPasswordURL = `${this.coreConfig.endpoint}auth/v1/forgotpassword`;
    this._silentLoginPermissionsURL = `${this.coreConfig.endpoint}auth/v1/silentlogin/permissions`;
  }

  silentLogin() {
    try {
      this.token = localStorage.getItem(this.coreConfig.token || 'access_token');
      this.username = new JwtHelperService().decodeToken(this.token).username;
      this.permissions = (window.localStorage.getItem('permissions') || '').split(',');
      this.isLoggedIn.next(true);
      this.httpClient.get<string[]>(this._silentLoginPermissionsURL).subscribe(s => {
        this.permissions = s;
      });
    } catch (e) {
      this.isLoggedIn.next(false);
      console.log('no login information');
    }
  }

  login(username: string, password: string, isCustomer = false): Observable<LoginResponse> {
    return this.httpClient.post<LoginResponse>(this._loginURL,
      {
        username, password
      },
      {
        params: isCustomer ? { t: 'customer' } : {}
      }
    ).pipe(
      tap(result => {
        this.token = result.token;
        this.username = username;
        this.permissions = result.permissions;
        this.roles = result.roles;
        if (isPlatformBrowser(this.platformId)) {
          localStorage.setItem(this.coreConfig.token || 'access_token', result.token);
          localStorage.setItem('permissions', result.permissions.join(','));
        }
        this.isLoggedIn.next(true);
      })
    );
  }

  logout(): Observable<any> {
    return this.httpClient.get(this._logoutURL).pipe(
      tap(() => { }, () => { }, () => {
        this.token = '';
        this.permissions = [];
        this.roles = [];
        localStorage.removeItem(this.coreConfig.token || 'access_token');
        this.isLoggedIn.next(false);
      }
      ));
  }

  changePassword(currentPassword: string, newPassword: string): Observable<any> {
    return this.httpClient.post(this._changePasswordURL, {
      currentPassword, newPassword
    });
  }

  register(customer: Customer): Observable<any> {
    return this.httpClient.post(this._registerURL, customer);
  }

  activate(username: string, activationToken: string): Observable<any> {
    return this.httpClient.post(this._activateURL, { username, activationToken });
  }

  resetActivationToken(username: string, oldToken: string): Observable<any> {
    return this.httpClient.post(this._resetActivationTokenURL, { username, oldToken });
  }

  forgotPassword(username: string): Observable<any> {
    return this.httpClient.get(this._forgotPasswordURL, {
      params: {
        username: username
      }
    });
  }

  resetPassword(username: string, token: string, password: string): Observable<any> {
    return this.httpClient.post(this._resetPasswordURL, { username, token, password });
  }

  can(permission: string) {
    return (this.permissions || []).findIndex(s => s === permission) !== -1;
  }

}
